// Types come from the @types/dagre devDependency. An ambient `declare module 'dagre'` in
// the app's own sources worked for `tsc -p`, whose include glob picks up any .d.ts, but not
// for the dev server: its watch-mode checker builds a program from the entry point and the
// modules it imports, and an ambient declaration nobody imports is not in that graph. A
// real @types package is resolved through the import itself, so both agree — and the
// declarations are genuine rather than a blanket `any`.
import dagre from 'dagre';

/**
 * Position React Flow nodes with dagre.
 *
 * This logic exists four other times in the app — AgentGraph.jsx:66,
 * WorkflowVisualization.tsx:127, TaskFlowEditor.jsx:404 and AgentFlowEditor.jsx:640 — with
 * only the rank direction and node dimensions differing. Those four are working editors and
 * are deliberately left alone; new callers should use this one, and the four can migrate
 * when something else takes them apart. (They each carry a `// @ts-ignore` above their
 * dagre import that @types/dagre has now made unnecessary; harmless, and theirs to drop.)
 *
 * Two differences from those copies, both deliberate:
 *
 *   - it does NOT mutate the nodes it is given. All four copies assign `node.position` in
 *     place and return the same array, which is safe only because each is called on a
 *     freshly-mapped list. Called on state, that is a mutation React cannot see;
 *   - dimensions are per-call rather than per-file constants, because a node's size is a
 *     property of the component being laid out, not of the layout algorithm.
 *
 * Note dagre is a LAYERED DAG algorithm. It runs on a cyclic graph without complaining but
 * spreads it poorly, so it suits a shallow neighbourhood far better than a dense mesh.
 */
export interface DagreLayoutOptions {
    nodeWidth: number;
    nodeHeight: number;
    /** 'LR' reads as a chain of consequences; 'TB' as a hierarchy. */
    rankdir?: 'LR' | 'TB';
    nodesep?: number;
    ranksep?: number;
    /**
     * A node's own size, where nodes differ (a small gate between two wide
     * workflow boxes). Unset, or returning nothing, means nodeWidth × nodeHeight.
     */
    sizeOf?: (node: LayoutableNode) => { width: number; height: number } | undefined;
    /**
     * The size of an edge's label, where it has one. dagre then gives the label a
     * column of its own between the ranks, so it is not drawn over the nodes on
     * either side. Unset, or returning nothing, reserves no room.
     */
    labelSizeOf?: (edge: LayoutableEdge) => { width: number; height: number } | undefined;
}

// Minimal structural constraints, and deliberately WITHOUT an index signature: an
// interface (React Flow's own Node and Edge, for instance) is not assignable to a type
// that has one, so adding `[key: string]: unknown` here would reject exactly the callers
// this util exists for.
export interface LayoutableNode {
    id: string;
    position?: { x: number; y: number };
}

export interface LayoutableEdge {
    source: string;
    target: string;
}

export const layoutWithDagre = <N extends LayoutableNode, E extends LayoutableEdge>(
    nodes: N[],
    edges: E[],
    { nodeWidth, nodeHeight, rankdir = 'LR', nodesep = 50, ranksep = 100, sizeOf, labelSizeOf }: DagreLayoutOptions,
): N[] => {
    const graph = new dagre.graphlib.Graph();
    graph.setGraph({ rankdir, align: 'UL', nodesep, ranksep, marginx: 40, marginy: 40 });
    graph.setDefaultEdgeLabel(() => ({}));

    const size = (node: LayoutableNode) => sizeOf?.(node) ?? { width: nodeWidth, height: nodeHeight };
    for (const node of nodes) graph.setNode(node.id, size(node));
    for (const edge of edges) {
        // An edge naming a node that is not on the canvas would make dagre invent one,
        // which then lays out as an invisible gap in the middle of the diagram.
        if (graph.hasNode(edge.source) && graph.hasNode(edge.target)) {
            const label = labelSizeOf?.(edge);
            // Centred on the edge: dagre's default ('r') offsets the label beside it.
            graph.setEdge(edge.source, edge.target, label ? { ...label, labelpos: 'c' } : {});
        }
    }

    dagre.layout(graph);

    return nodes.map((node) => {
        const positioned = graph.node(node.id);
        if (!positioned) return node;
        return {
            ...node,
            // dagre reports a centre; React Flow wants a top-left corner.
            position: { x: positioned.x - size(node).width / 2, y: positioned.y - size(node).height / 2 },
        };
    });
};

export default layoutWithDagre;
